import React, { useEffect, useState, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import {
    BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, PieChart, Pie, AreaChart, Area
} from "recharts"
import { useAIHistory } from '../../hooks/useAIHistory'
import useProfilingHistory from '../../hooks/useProfilingHistory'
import { authHeaders } from '../../utils/authFetch'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

const C = {
    espresso: "transparent",
    dark: "#F9FAFB",
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    amber: "#D97706",
    gold: "#B45309",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
    indigo: "#4F46E5",
}

const LIFECYCLE_STAGES = {
    Champion: { color: C.green, label: "Champion", desc: "Bought recently, often & high value" },
    Loyal: { color: C.teal, label: "Loyal", desc: "Regular buyers, good frequency" },
    Promising: { color: C.blue, label: "Promising", desc: "Recent buyers with potential" },
    "At Risk": { color: C.amber, label: "At Risk", desc: "Was loyal, hasn't returned lately" },
    Dormant: { color: C.coral, label: "Dormant", desc: "Long inactive, low engagement" },
    New: { color: C.purple, label: "New", desc: "First-time buyers" },
}

const PERSONALITY_CONFIG = {
    "Loyal Regular": { icon: "🏆", color: "#0D9488", bg: "#0D948815" },
    "High-Value Spender": { icon: "💎", color: "#B45309", bg: "#B4530915" },
    "Occasional Visitor": { icon: "🌙", color: "#7C3AED", bg: "#7C3AED15" },
    "Trend Follower": { icon: "✨", color: "#2563EB", bg: "#2563EB15" },
    "Bargain Hunter": { icon: "🎯", color: "#DC2626", bg: "#DC262615" },
    "Early Adopter": { icon: "🚀", color: "#16A34A", bg: "#16A34A15" },
    "Weekend Warrior": { icon: "⚡", color: "#D97706", bg: "#D9770615" },
    "Silent Loyalist": { icon: "🤫", color: "#6B7280", bg: "#6B728015" },
}

const fmt = (n) => `₱${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 0 })}`
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
const toPHT = (isoStr) => new Date(new Date(isoStr).getTime() + PHT_OFFSET_MS)
const daysSince = (isoStr) => {
    const now = new Date()
    return Math.floor((now - new Date(isoStr)) / 86400000)
}
const fmtDate = (d) => new Date(d).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })
const fmtMonth = (iso) => toPHT(iso).toLocaleDateString("en-PH", { month: "short", timeZone: "UTC" })
const hasData = (d) => Array.isArray(d) && d.length > 0

function classifyLifecycle(recencyDays, frequency, monetaryTotal) {
    if (frequency === 1 && recencyDays <= 30) return "New"
    if (recencyDays <= 14 && frequency >= 5 && monetaryTotal >= 3000) return "Champion"
    if (recencyDays <= 30 && frequency >= 3) return "Loyal"
    if (recencyDays <= 30) return "Promising"
    if (recencyDays <= 90 && frequency >= 2) return "At Risk"
    return "Dormant"
}

function churnScore(recencyDays, frequency, avgDaysBetween) {
    const recencyScore = Math.min(recencyDays / 120, 1) * 50
    const freqScore = Math.max(0, 1 - frequency / 10) * 20
    const gapScore = avgDaysBetween > 0 ? Math.min(recencyDays / (avgDaysBetween * 2), 1) * 30 : 30
    return Math.round(recencyScore + freqScore + gapScore)
}

const getRiskConfig = (riskFactor) => {
    if (!riskFactor || riskFactor === "None" || riskFactor === "none") return null
    const lower = riskFactor.toLowerCase()
    if (lower.includes("high") || lower.includes("churn") || lower.includes("lost"))
        return { color: "#DC2626", bg: "#DC262612", icon: "🔴" }
    if (lower.includes("medium") || lower.includes("declining") || lower.includes("infrequent"))
        return { color: "#D97706", bg: "#D9770612", icon: "🟡" }
    return { color: "#0D9488", bg: "#0D948812", icon: "🟢" }
}

const parseProfileText = (text) => {
    if (!text) return []
    return text
        .split(/(?<=[.!?])\s+|\n+/)
        .map(s => s.trim())
        .filter(s => s.length > 10)
        .filter(s => !s.startsWith('#'))
}

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ 
            background: C.card, 
            borderColor: C.border,
            flex: 1,        
            display: "flex",
            flexDirection: "column",
        }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const EmptyState = ({ message = "No data available.", height = 200 }) => (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed"
        style={{ height, borderColor: C.border }}>
        <p className="m-0 text-[13px] text-center max-w-[240px]" style={{ color: C.muted }}>{message}</p>
    </div>
)

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
        <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
            style={{ background: "#fff", borderColor: C.border }}>
            <p className="m-0 mb-1.5 font-semibold" style={{ color: C.text }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="m-0 my-0.5" style={{ color: p.color || C.textSoft }}>
                    {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    )
}

const ChurnBadge = ({ score }) => {
    const level = score >= 70 ? { label: "High Risk", color: C.coral }
        : score >= 40 ? { label: "Medium", color: C.amber }
        : { label: "Healthy", color: C.green }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: level.color + "22", color: level.color }}>
            {level.label}
        </span>
    )
}

const StageChip = ({ stage }) => {
    const cfg = LIFECYCLE_STAGES[stage] ?? { color: C.muted, label: stage }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.color + "22", color: cfg.color }}>
            {cfg.label}
        </span>
    )
}


function useRawData() {
    const [customers, setCustomers] = useState(null)
    const [orders, setOrders] = useState(null)

    useEffect(() => {
        fetch(`${BASE_URL}/api/customer-intelligence/customers`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => setCustomers(data ?? []))
            .catch(err => { console.error('customers fetch error:', err); setCustomers([]) })
    }, [])

    useEffect(() => {
        fetch(`${BASE_URL}/api/customer-intelligence/orders`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => setOrders(data ?? []))
            .catch(err => { console.error('orders fetch error:', err); setOrders([]) })
    }, [])

    return { customers, orders }
}

function buildCustomerProfiles(customers, orders) {
    if (!customers || !orders) return null

    const ordersByCustomer = {}
    for (const o of orders) {
        if (!o.user_id) continue
        if (!ordersByCustomer[o.user_id]) ordersByCustomer[o.user_id] = []
        ordersByCustomer[o.user_id].push(o)
    }

    return customers.map((c) => {
        const customerOrders = (ordersByCustomer[c.user_id] ?? []).sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
        )
        const frequency = customerOrders.length
        const totalSpent = customerOrders.reduce((s, o) => s + Number(o.final_amount), 0)
        const avgOrderValue = frequency > 0 ? totalSpent / frequency : 0
        const lastOrderAt = frequency > 0 ? customerOrders[customerOrders.length - 1].created_at : null
        const firstOrderAt = frequency > 0 ? customerOrders[0].created_at : null
        const recencyDays = lastOrderAt ? daysSince(lastOrderAt) : 9999

        let avgGap = 0
        if (frequency >= 2) {
            const gaps = []
            for (let i = 1; i < customerOrders.length; i++) {
                gaps.push(
                    (new Date(customerOrders[i].created_at) - new Date(customerOrders[i - 1].created_at)) / 86400000
                )
            }
            avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length
        }

        const stage = frequency === 0 ? "Dormant" : classifyLifecycle(recencyDays, frequency, totalSpent)
        const churn = frequency === 0 ? 100 : churnScore(recencyDays, frequency, avgGap)

        const tenureMonths = firstOrderAt
            ? Math.max(1, (Date.now() - new Date(firstOrderAt)) / (30 * 86400000))
            : 1
        const monthlySpend = totalSpent / tenureMonths
        const projectedLTV = monthlySpend * 12

        return {
            ...c,
            fullName: `${c.first_name} ${c.last_name}`,
            orders: customerOrders,
            frequency,
            totalSpent,
            avgOrderValue,
            lastOrderAt,
            firstOrderAt,
            recencyDays,
            avgGapDays: Math.round(avgGap),
            stage,
            churnScore: churn,
            projectedLTV,
            tenureMonths: Math.round(tenureMonths),
        }
    })
}

const SearchBar = ({ value, onChange }) => (
    <div className="relative">
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Search customer name or email…"
            className="w-full rounded-xl border px-4 py-2 text-xs pr-8 outline-none"
            style={{ borderColor: C.border, color: C.text, background: C.card }}
        />
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
            fill="none" stroke={C.muted} strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
        </svg>
    </div>
)

const StageFilter = ({ selected, onChange }) => (
    <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-[11px] uppercase tracking-wider" style={{ color: C.muted }}>Stage</span>
        <button onClick={() => onChange(null)}
            className="px-3 py-1 rounded-full text-[11px] border transition-colors"
            style={{
                background: selected === null ? C.amber : C.card,
                borderColor: selected === null ? C.amber : C.border,
                color: selected === null ? "#fff" : C.textSoft,
                cursor: "pointer",
            }}>All</button>
        {Object.entries(LIFECYCLE_STAGES).map(([key, cfg]) => (
            <button key={key} onClick={() => onChange(key)}
                className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                style={{
                    background: selected === key ? cfg.color : C.card,
                    borderColor: selected === key ? cfg.color : C.border,
                    color: selected === key ? "#fff" : C.textSoft,
                    cursor: "pointer",
                }}>{cfg.label}</button>
        ))}
    </div>
)

const AIProfileCard = ({ profile }) => {
    const [expanded, setExpanded] = React.useState(false)
    if (!profile) return null
 
    const personalityKey = Object.keys(PERSONALITY_CONFIG).find(k =>
        profile.personality?.toLowerCase().includes(k.toLowerCase())
    )
    const personalityCfg = PERSONALITY_CONFIG[personalityKey] ?? {
        icon: "👤",
        color: "#7C3AED",
        bg: "#7C3AED15",
    }
    const riskCfg = getRiskConfig(profile.riskFactor)
    const sentences = parseProfileText(profile.profileCard)
    const preview = sentences.slice(0, 2)
    const rest = sentences.slice(2)
 
    return (
        <div
            style={{
                marginTop: 14,
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${personalityCfg.color}30`,
                background: "#FAFAFA",
                fontFamily: "'DM Sans', system-ui, sans-serif",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    background: personalityCfg.bg,
                    borderBottom: `1px solid ${personalityCfg.color}20`,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${personalityCfg.color}cc, ${personalityCfg.color}44)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            flexShrink: 0,
                            boxShadow: `0 2px 8px ${personalityCfg.color}40`,
                        }}
                    >
                        {personalityCfg.icon}
                    </div>
                    <div>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: personalityCfg.color, letterSpacing: "0.01em" }}>
                            {profile.personality ?? "Customer Profile"}
                        </p>
                        <p style={{ margin: 0, fontSize: 10, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            AI Profile Card
                        </p>
                    </div>
                </div>
                <div style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase",
                    color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 50, padding: "3px 8px",
                }}>
                    ✦ Gemini
                </div>
            </div>
 
            <div style={{ padding: "12px 14px 0" }}>
                {preview.map((sentence, i) => (
                    <p key={i} style={{ margin: "0 0 6px", fontSize: 12.5, lineHeight: 1.65, color: "#374151" }}>
                        {sentence}
                    </p>
                ))}
                {rest.length > 0 && (
                    <>
                        <div style={{ maxHeight: expanded ? 400 : 0, overflow: "hidden", transition: "max-height 0.3s ease" }}>
                            {rest.map((sentence, i) => (
                                <p key={i} style={{ margin: "0 0 6px", fontSize: 12.5, lineHeight: 1.65, color: "#374151" }}>
                                    {sentence}
                                </p>
                            ))}
                        </div>
                        <button
                            onClick={() => setExpanded(v => !v)}
                            style={{
                                background: "none", border: "none", padding: "2px 0 8px", fontSize: 11,
                                color: personalityCfg.color, cursor: "pointer", fontWeight: 600,
                                display: "flex", alignItems: "center", gap: 3,
                            }}>
                            {expanded ? "↑ Show less" : `↓ Read more (${rest.length} more insights)`}
                        </button>
                    </>
                )}
            </div>
 
            <div style={{
                display: "flex", gap: 6, flexWrap: "wrap",
                padding: "8px 14px 12px", borderTop: `1px solid #F3F4F6`,
                marginTop: rest.length === 0 ? 8 : 0,
            }}>
                {profile.action && (
                    <div style={{
                        display: "flex", alignItems: "center", gap: 5,
                        background: "#EFF6FF", border: "1px solid #BFDBFE",
                        borderRadius: 50, padding: "4px 10px", fontSize: 10.5, fontWeight: 600, color: "#1D4ED8", maxWidth: "100%",
                    }}>
                        <span>💡</span>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                            {profile.action}
                        </span>
                    </div>
                )}
                {riskCfg && (
                    <div style={{
                        display: "flex", alignItems: "center", gap: 5,
                        background: riskCfg.bg, border: `1px solid ${riskCfg.color}30`,
                        borderRadius: 50, padding: "4px 10px", fontSize: 10.5, fontWeight: 600, color: riskCfg.color, maxWidth: "100%",
                    }}>
                        <span>{riskCfg.icon}</span>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                            {profile.riskFactor}
                        </span>
                    </div>
                )}
                {!riskCfg && !profile.action && (
                    <div style={{
                        display: "flex", alignItems: "center", gap: 5,
                        background: "#F0FDF4", border: "1px solid #BBF7D0",
                        borderRadius: 50, padding: "4px 10px", fontSize: 10.5, fontWeight: 600, color: "#16A34A",
                    }}>
                        <span>✓</span> No risk signals
                    </div>
                )}
            </div>
        </div>
    )
}

const CustomerHistoryTable = ({ profiles, loading, profilesCache }) => {
    const [search, setSearch] = useState("")
    const [stageFilter, setStageFilter] = useState(null)
    const [selected, setSelected] = useState(null)
    const [page, setPage] = useState(0)
    const [sortBy, setSortBy] = useState("spent") // 'spent' | 'alpha'
    const PAGE_SIZE = 10

    const filtered = useMemo(() => {
        if (!profiles) return []
        return profiles
            .filter((p) => p.frequency > 0)
            .filter((p) => !stageFilter || p.stage === stageFilter)
            .filter((p) => {
                const q = search.toLowerCase()
                return !q || p.fullName.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q)
            })
            .sort((a, b) =>
                sortBy === "alpha"
                    ? a.fullName.localeCompare(b.fullName)
                    : b.totalSpent - a.totalSpent
            )
    }, [profiles, search, stageFilter, sortBy])

    const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)

    if (loading) return <Card><SectionTitle title="Customer Purchase History" /><Spinner height={300} /></Card>

    return (
        <Card>
            <SectionTitle title="Customer Purchase History" subtitle="All registered customers with completed orders" />
            <div className="flex flex-col gap-3 mb-4">
                <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(0) }} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <StageFilter selected={stageFilter} onChange={(v) => { setStageFilter(v); setPage(0) }} />
                    <div className="flex items-center gap-1.5">
                        <span className="text-[11px] uppercase tracking-wider" style={{ color: C.muted }}>Sort</span>
                        {[
                            { key: "spent", label: "Highest Spent" },
                            { key: "alpha", label: "A–Z" },
                        ].map((opt) => (
                            <button
                                key={opt.key}
                                onClick={() => { setSortBy(opt.key); setPage(0) }}
                                className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                                style={{
                                    background: sortBy === opt.key ? C.amber : C.card,
                                    borderColor: sortBy === opt.key ? C.amber : C.border,
                                    color: sortBy === opt.key ? "#fff" : C.textSoft,
                                    cursor: "pointer",
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            {["Customer", "Stage", "Orders", "Total Spent", "Avg Order", "Last Purchase", "Recency"].map((h) => (
                                <th key={h} className="text-left py-2 px-2 font-semibold uppercase tracking-wide"
                                    style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.map((p) => (
                            <React.Fragment key={p.user_id}>
                                <tr
                                    onClick={() => setSelected(selected?.user_id === p.user_id ? null : p)}
                                    className="transition-colors"
                                    style={{
                                        borderBottom: `1px solid ${C.border}`,
                                        cursor: "pointer",
                                        background: selected?.user_id === p.user_id ? C.amber + "0a" : "transparent",
                                    }}
                                >
                                    <td className="py-2.5 px-2">
                                        <p className="m-0 font-semibold" style={{ color: C.text }}>{p.fullName}</p>
                                        <p className="m-0" style={{ color: C.muted, fontSize: 10 }}>{p.email}</p>
                                    </td>
                                    <td className="py-2.5 px-2"><StageChip stage={p.stage} /></td>
                                    <td className="py-2.5 px-2 font-bold" style={{ color: C.blue }}>{p.frequency}</td>
                                    <td className="py-2.5 px-2 font-bold" style={{ color: C.green }}>{fmt(p.totalSpent)}</td>
                                    <td className="py-2.5 px-2" style={{ color: C.textSoft }}>{fmt(p.avgOrderValue)}</td>
                                    <td className="py-2.5 px-2" style={{ color: C.textSoft }}>
                                        {p.lastOrderAt ? fmtDate(p.lastOrderAt) : "—"}
                                    </td>
                                    <td className="py-2.5 px-2">
                                        <span style={{ color: p.recencyDays <= 14 ? C.green : p.recencyDays <= 45 ? C.amber : C.coral }}>
                                            {p.recencyDays === 9999 ? "—" : `${p.recencyDays}d ago`}
                                        </span>
                                    </td>
                                </tr>
                                {selected?.user_id === p.user_id && (
                                    <tr>
                                        <td colSpan={7} className="px-2 pb-3">
                                            <div className="rounded-xl p-3 mt-1" style={{ background: C.dark }}>
                                                <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.muted }}>
                                                    Order History — {p.fullName}
                                                </p>
                                                <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                                                    {[...p.orders].reverse().map((o) => (
                                                        <div key={o.order_id}
                                                            className="flex justify-between items-center rounded-lg px-2.5 py-1.5"
                                                            style={{ background: C.card, border: `1px solid ${C.border}` }}>
                                                            <span style={{ color: C.textSoft, fontSize: 11 }}>
                                                                {fmtDate(o.created_at)}
                                                            </span>
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded"
                                                                style={{ background: C.border, color: C.textSoft }}>
                                                                {o.order_source ?? "—"}
                                                            </span>
                                                            <span className="font-bold" style={{ color: C.amber, fontSize: 12 }}>
                                                                {fmt(o.final_amount)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                {profilesCache && profilesCache[p.user_id] && (
                                                    <AIProfileCard profile={profilesCache[p.user_id]} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {pageData.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-xs" style={{ color: C.muted }}>No customers match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3">
                    <span className="text-[11px]" style={{ color: C.muted }}>
                        {filtered.length} customers · Page {page + 1} of {totalPages}
                    </span>
                    <div className="flex gap-1.5">
                        <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                            className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                            style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>← Prev</button>
                        <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                            className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                            style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>Next →</button>
                    </div>
                </div>
            )}
        </Card>
    )
}

const PurchasePatternAnalysis = ({ orders, loading }) => {
    const { monthlyData, sourceData, dayData, topKPIs } = useMemo(() => {
        if (!hasData(orders)) return { monthlyData: [], sourceData: [], dayData: [], topKPIs: null }

        const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
        const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]

        const monthMap = {}, sourceMap = {}, dayMap = {}

        for (const o of orders) {
            const month = fmtMonth(o.created_at)
            const src = o.order_source ?? "pos"
            const day = DAYS[toPHT(o.created_at).getUTCDay()]

            if (!monthMap[month]) monthMap[month] = { month, revenue: 0, orders: 0 }
            monthMap[month].revenue += Number(o.final_amount)
            monthMap[month].orders  += 1

            if (!sourceMap[src]) sourceMap[src] = { source: src, orders: 0, revenue: 0 }
            sourceMap[src].orders  += 1
            sourceMap[src].revenue += Number(o.final_amount)

            if (!dayMap[day]) dayMap[day] = { day, orders: 0 }
            dayMap[day].orders += 1
        }

        const monthlyData = Object.values(monthMap).sort((a, b) =>
            MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month))

        const sourceData = Object.values(sourceMap).sort((a, b) => b.orders - a.orders)
        const dayData = DAYS.map((d) => dayMap[d] ?? { day: d, orders: 0 })

        const totalOrders = orders.length
        const totalRevenue = orders.reduce((s, o) => s + Number(o.final_amount), 0)
        const peakDay = dayData.reduce((a, b) => (a.orders > b.orders ? a : b), dayData[0])
        const peakMonth = monthlyData.reduce((a, b) => (a.revenue > b.revenue ? a : b), monthlyData[0])

        return {
            monthlyData,
            sourceData,
            dayData,
            topKPIs: { totalOrders, totalRevenue, peakDay: peakDay?.day, peakMonth: peakMonth?.month },
        }
    }, [orders])

    if (loading) return <Card><SectionTitle title="Purchase Pattern Analysis" /><Spinner height={300} /></Card>

    return (
        <Card>
            <SectionTitle title="Purchase Pattern Analysis" subtitle="Order trends by time, day, and channel" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                <KPI label="Total Orders" value={topKPIs?.totalOrders?.toLocaleString() ?? "—"} color={C.blue} />
                <KPI label="Total Revenue" value={topKPIs ? fmt(topKPIs.totalRevenue) : "—"} color={C.green} />
                <KPI label="Busiest Day" value={topKPIs?.peakDay ?? "—"} sub="highest order volume" color={C.amber} />
                <KPI label="Best Month" value={topKPIs?.peakMonth ?? "—"} sub="highest revenue" color={C.teal} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Monthly Revenue Trend</p>
                    <ResponsiveContainer debounce={200} width="100%" height={200}>
                        <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="ciRevGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={C.teal} stopOpacity={0.4} />
                                    <stop offset="95%" stopColor={C.teal} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.muted, fontSize: 9 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="revenue" name="Revenue" stroke={C.teal} fill="url(#ciRevGrad)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Orders by Day of Week</p>
                    <ResponsiveContainer debounce={200} width="100%" height={200}>
                        <BarChart data={dayData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="day" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="orders" name="Orders" radius={[4,4,0,0]}>
                                {dayData.map((d) => (
                                    <Cell key={d.day}
                                        fill={d.orders === Math.max(...dayData.map(x => x.orders)) ? C.amber : C.amber + "55"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="col-span-2">
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Order Source Breakdown</p>
                    <div className="flex gap-3 flex-wrap">
                        {sourceData.map((s, i) => {
                            const colors = [C.blue, C.teal, C.amber, C.purple, C.coral]
                            const pct = ((s.orders / (topKPIs?.totalOrders || 1)) * 100).toFixed(1)
                            return (
                                <div key={s.source} className="flex-1 min-w-[120px] rounded-xl p-3 border"
                                    style={{ borderColor: C.border, background: C.dark }}>
                                    <p className="m-0 text-[11px] font-semibold capitalize" style={{ color: colors[i % colors.length] }}>
                                        {s.source.replace("_", " ")}
                                    </p>
                                    <p className="m-0 text-[20px] font-bold" style={{ color: C.text }}>{s.orders}</p>
                                    <p className="m-0 text-[11px]" style={{ color: C.muted }}>{pct}% · {fmt(s.revenue)}</p>
                                    <div className="mt-2 h-1 rounded-full" style={{ background: C.border }}>
                                        <div className="h-1 rounded-full" style={{
                                            width: `${pct}%`,
                                            background: colors[i % colors.length],
                                        }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </Card>
    )
}

const LifecycleClassification = ({ profiles, loading }) => {
    const { stageCounts, stageRevenue } = useMemo(() => {
        if (!profiles) return { stageCounts: [], stageRevenue: [] }
        const map = {}
        for (const p of profiles) {
            if (!map[p.stage]) map[p.stage] = { stage: p.stage, count: 0, revenue: 0, avgLTV: 0, _ltv: [] }
            map[p.stage].count   += 1
            map[p.stage].revenue += p.totalSpent
            map[p.stage]._ltv.push(p.projectedLTV)
        }
        return {
            stageCounts: Object.values(map).map((s) => ({
                ...s,
                avgLTV: s._ltv.reduce((a, b) => a + b, 0) / s._ltv.length,
            })).sort((a, b) => b.count - a.count),
            stageRevenue: Object.values(map).map((s) => ({ name: s.stage, value: s.revenue })),
        }
    }, [profiles])

    if (loading) return <Card><SectionTitle title="Customer Lifecycle Stages" /><Spinner height={300} /></Card>

    const total = stageCounts.reduce((s, d) => s + d.count, 0)

    return (
        <Card>
            <SectionTitle title="Customer Lifecycle Classification" subtitle="RFM-based segmentation — recency, frequency, monetary value" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">                <div className="flex flex-col h-full">
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Stage Distribution</p>
                    <div className="flex-1 flex items-center justify-center" style={{ minHeight: 300 }}>
                        <ResponsiveContainer debounce={200} width="100%" height={300}>
                            <PieChart>
                                <Pie data={stageRevenue} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={115} innerRadius={65}
                                    paddingAngle={3}>
                                    {stageRevenue.map((s, i) => (
                                        <Cell key={s.name} fill={LIFECYCLE_STAGES[s.name]?.color ?? C.muted} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => fmt(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-1" style={{ color: C.muted }}>Stage Breakdown</p>
                    {stageCounts.map((s) => {
                        const cfg = LIFECYCLE_STAGES[s.stage] ?? { color: C.muted }
                        const pct = ((s.count / total) * 100).toFixed(0)
                        return (
                            <div key={s.stage} className="rounded-lg px-3 py-2 border"
                                style={{ borderColor: C.border, background: C.dark }}>
                                <div className="flex justify-between items-center mb-1.5">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
                                        <span className="font-semibold text-xs" style={{ color: C.text }}>{s.stage}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px]" style={{ color: C.muted }}>{s.count} customers</span>
                                        <span className="font-bold text-xs" style={{ color: cfg.color }}>{pct}%</span>
                                    </div>
                                </div>
                                <div className="flex justify-between text-[10px] mb-1.5" style={{ color: C.muted }}>
                                    <span>Revenue: <strong style={{ color: C.text }}>{fmt(s.revenue)}</strong></span>
                                    <span>Avg LTV: <strong style={{ color: cfg.color }}>{fmt(s.avgLTV)}</strong></span>
                                </div>
                                <div className="h-1 rounded-full" style={{ background: C.border }}>
                                    <div className="h-1 rounded-full transition-all" style={{ width: `${pct}%`, background: cfg.color }} />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(LIFECYCLE_STAGES).map(([key, cfg]) => (
                    <div key={key} className="flex items-start gap-2 rounded-lg p-2.5 border"
                        style={{ borderColor: cfg.color + "44", background: cfg.color + "0a" }}>
                        <div className="w-2 h-2 rounded-full mt-0.5 shrink-0" style={{ background: cfg.color }} />
                        <div>
                            <p className="m-0 text-[11px] font-semibold" style={{ color: cfg.color }}>{cfg.label}</p>
                            <p className="m-0 text-[10px]" style={{ color: C.muted }}>{cfg.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    )
}

const EmailIcon = ({ width = 12, height = 12, stroke = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
    </svg>
)

function getTopDrink(orders) {
    if (!orders || orders.length === 0) return null
    const nameCount = {}
    for (const o of orders) {
        const name = o.product_name || o.item_name || o.name || null
        if (name) nameCount[name] = (nameCount[name] || 0) + 1
    }
    if (Object.keys(nameCount).length === 0) return null
    return Object.entries(nameCount).sort((a, b) => b[1] - a[1])[0][0]
}

function buildEmailHTML({ name, topDrink, recencyDays, frequency, totalSpent }) {
    const firstName = name?.split(" ")[0] ?? "there"
    const drinkLabel = topDrink ?? "your favourite drink"
    const spentFormatted = Number(totalSpent ?? 0).toLocaleString("en-PH", { minimumFractionDigits: 0 })
    const year = new Date().getFullYear()

    return `<!DOCTYPE html>
    <html lang="en">
    <head>
    <meta charset="UTF-8"/>
    <meta name="viewport" content="width=device-width,initial-scale=1.0"/>
    <title>We miss you at Coffee Gusto</title>
    </head>
    <body style="margin:0;padding:0;background-color:#F5EFE6;font-family:Arial,Helvetica,sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5EFE6;padding:24px 16px;">
    <tr><td align="center">
    <table role="presentation" width="540" cellpadding="0" cellspacing="0" style="max-width:540px;width:100%;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.12);">

    <!-- Gold top bar -->
    <tr><td style="background:linear-gradient(90deg,#7C2D00,#D97706,#7C2D00);height:4px;font-size:0;line-height:0;">&nbsp;</td></tr>

    <!-- Header -->
    <tr>
        <td style="background-color:#1C0A00;padding:32px 32px 24px;text-align:center;">
        <div style="display:inline-block;width:52px;height:52px;border-radius:50%;background:#D97706;font-size:26px;line-height:52px;margin-bottom:12px;">☕</div><br/>
        <span style="font-family:Georgia,'Times New Roman',serif;font-size:26px;color:#FDF6EE;letter-spacing:0.05em;">Coffee Gusto</span><br/>
        <span style="font-family:Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#D97706;display:inline-block;margin-top:6px;">— We Miss You —</span>
        </td>
    </tr>

    <!-- Rounded white section -->
    <tr>
        <td style="background-color:#1C0A00;padding:0;">
        <div style="background:#ffffff;border-radius:24px 24px 0 0;padding:28px 32px 0;">

            <p style="margin:0 0 8px;font-family:Georgia,'Times New Roman',serif;font-size:20px;color:#1C0A00;">
            Hey <strong>${firstName}</strong> — it's been a while! 👋
            </p>
            <p style="margin:0 0 20px;font-family:Arial,sans-serif;font-size:14px;line-height:1.8;color:#5C3D2A;">
            We noticed you haven't stopped by Coffee Gusto in
            <strong style="color:#1C0A00;">${recencyDays} days</strong>.
            Your favourite spot has been saving a seat — and your perfect brew is ready whenever you are.
            </p>

            <!-- Drink card -->
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
            <tr>
                <td style="background:#FDF6EE;border:2px solid #D97706;border-radius:12px;overflow:hidden;">
                <div style="background:#D97706;padding:7px 16px;">
                    <span style="font-family:Arial,sans-serif;font-size:9px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#1C0A00;">☕&nbsp;&nbsp;Your Go-To Order</span>
                </div>
                <div style="padding:16px 20px;display:flex;align-items:center;gap:14px;">
                    <span style="font-size:36px;">☕</span>
                    <div>
                    <p style="margin:0 0 4px;font-family:Georgia,'Times New Roman',serif;font-size:18px;font-weight:700;color:#1C0A00;">${drinkLabel}</p>
                    <p style="margin:0;font-family:Arial,sans-serif;font-size:11px;color:#8B6347;">
                        Ordered <strong style="color:#5C3D2A;">${frequency}x</strong>
                        &nbsp;·&nbsp;
                        <strong style="color:#5C3D2A;">&#8369;${spentFormatted}</strong> lifetime spend
                    </p>
                    </div>
                </div>
                </td>
            </tr>
            </table>

            <p style="margin:0 0 24px;font-family:Arial,sans-serif;font-size:14px;line-height:1.8;color:#5C3D2A;">
            Your <strong style="color:#1C0A00;">${drinkLabel}</strong> hasn't forgotten you.
            Come back, treat yourself — your KaPerk rewards are waiting. ☕
            </p>

            <!-- CTA button -->
            <table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 auto 28px;">
            <tr>
                <td style="background-color:#1C0A00;border-radius:50px;box-shadow:0 4px 12px rgba(28,10,0,0.25);">
                <a href="#" style="display:inline-block;padding:13px 36px;font-family:Arial,sans-serif;font-size:13px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#FDF6EE;text-decoration:none;">
                    Order via KaPerk &rarr;
                </a>
                </td>
            </tr>
            </table>

            <!-- Perks row -->
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:0;">
            <tr>
                <td width="32%" style="text-align:center;padding:14px 8px;background:#FDF6EE;border:1px solid #E8D5B7;border-radius:10px 0 0 10px;">
                <div style="font-size:20px;margin-bottom:5px;">🎁</div>
                <p style="margin:0 0 2px;font-family:Arial,sans-serif;font-size:11px;font-weight:700;color:#1C0A00;">Earn Points</p>
                <p style="margin:0;font-family:Arial,sans-serif;font-size:10px;color:#8B6347;">Every sip counts</p>
                </td>
                <td width="2%" style="background:#E8D5B7;"></td>
                <td width="32%" style="text-align:center;padding:14px 8px;background:#FDF6EE;border-top:1px solid #E8D5B7;border-bottom:1px solid #E8D5B7;">
                <div style="font-size:20px;margin-bottom:5px;">☕</div>
                <p style="margin:0 0 2px;font-family:Arial,sans-serif;font-size:11px;font-weight:700;color:#1C0A00;">Fresh Brews</p>
                <p style="margin:0;font-family:Arial,sans-serif;font-size:10px;color:#8B6347;">New menu items</p>
                </td>
                <td width="2%" style="background:#E8D5B7;"></td>
                <td width="32%" style="text-align:center;padding:14px 8px;background:#FDF6EE;border:1px solid #E8D5B7;border-radius:0 10px 10px 0;">
                <div style="font-size:20px;margin-bottom:5px;">📍</div>
                <p style="margin:0 0 2px;font-family:Arial,sans-serif;font-size:11px;font-weight:700;color:#1C0A00;">Find a Branch</p>
                <p style="margin:0;font-family:Arial,sans-serif;font-size:10px;color:#8B6347;">Multiple locations</p>
                </td>
            </tr>
            </table>

        </div>
        </td>
    </tr>

    <!-- Footer -->
    <tr>
        <td style="background-color:#1C0A00;padding:20px 32px;text-align:center;">
        <p style="margin:0 0 4px;font-family:Arial,sans-serif;font-size:11px;color:#8B6347;">
            &copy; ${year} Coffee Gusto &nbsp;&middot;&nbsp; Powered by KaPerk
        </p>
        <p style="margin:0;font-family:Arial,sans-serif;font-size:10px;color:#5C3D2A;">
            You're receiving this because you're a valued customer.
            &nbsp;<a href="#" style="color:#D97706;">Unsubscribe</a>
        </p>
        </td>
    </tr>

    <!-- Gold bottom bar -->
    <tr><td style="background:linear-gradient(90deg,#7C2D00,#D97706,#7C2D00);height:4px;font-size:0;line-height:0;">&nbsp;</td></tr>

    </table>
    </td></tr>
    </table>
    </body>
    </html>`
}

const EmailPreview = ({ customer }) => {
    const iframeRef = React.useRef(null)
    const [iframeHeight, setIframeHeight] = React.useState(600)

    React.useEffect(() => {
        const handler = (e) => {
            if (e.data?.type === 'iframeHeight' && typeof e.data.height === 'number') {
                setIframeHeight(e.data.height + 4)
            }
        }
        window.addEventListener('message', handler)
        return () => window.removeEventListener('message', handler)
    }, [])

    if (!customer) return null

    const topDrink = getTopDrink(customer.orders)
    const html = buildEmailHTML({
        name:        customer.fullName,
        topDrink,
        recencyDays: customer.recencyDays,
        frequency:   customer.frequency,
        totalSpent:  customer.totalSpent,
    })

    const htmlWithHeightReporter = html.replace(
        '</body>',
        `<script>
            (function () {
                function report() {
                    window.parent.postMessage(
                        { type: 'iframeHeight', height: document.body.scrollHeight },
                        '*'
                    );
                }
                report();
                window.addEventListener('load', report);
            })();
        </script></body>`
    )

    return (
        <div style={{
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
            background: '#F5EFE6',
            maxWidth: '100%',
        }}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <iframe
                    ref={iframeRef}
                    srcDoc={htmlWithHeightReporter}
                    title="Email Preview"
                    scrolling="no"
                    style={{
                        width: 540,
                        height: iframeHeight,
                        border: 'none',
                        display: 'block',
                    }}
                />
            </div>
        </div>
    )
}

const ChurnDetection = ({ profiles, loading }) => {
    const [emailModal, setEmailModal] = useState(null)
    const [emailLoading, setEmailLoading] = useState(false)
    const [emailResults, setEmailResults] = useState([])
    const [emailDone, setEmailDone] = useState(false)
    const [previewCustomer, setPreviewCustomer] = useState(null)
    const [modalTab, setModalTab] = useState("preview") 
    const [atRiskPage, setAtRiskPage] = useState(0)
    const AT_RISK_PAGE_SIZE = 5 

    const closeModal = () => {
        setEmailModal(null)
        setEmailResults([])
        setEmailDone(false)
        setPreviewCustomer(null)
        setModalTab("preview")
    }

    React.useEffect(() => {
        if (!emailModal) return
        if (!emailModal.bulk) setPreviewCustomer(emailModal.customer)
        else setPreviewCustomer(emailModal.customers[0] ?? null)
    }, [emailModal])

    const handleSendEmails = async () => {
        setEmailLoading(true)
        try {
            const customers = emailModal.bulk
                ? emailModal.customers.map(c => ({
                    name: c.fullName,
                    email: c.email,
                    recencyDays: c.recencyDays,
                    frequency: c.frequency,
                    totalSpent: c.totalSpent,
                    churnScore: c.churnScore,
                    topDrink: getTopDrink(c.orders),
                    emailHtml: buildEmailHTML({
                        name: c.fullName,
                        topDrink: getTopDrink(c.orders),
                        recencyDays: c.recencyDays,
                        frequency: c.frequency,
                        totalSpent: c.totalSpent,
                    }),
                }))
                : [{
                    name: emailModal.customer.fullName,
                    email: emailModal.customer.email,
                    recencyDays: emailModal.customer.recencyDays,
                    frequency: emailModal.customer.frequency,
                    totalSpent: emailModal.customer.totalSpent,
                    churnScore: emailModal.customer.churnScore,
                    topDrink: getTopDrink(emailModal.customer.orders),
                    emailHtml: buildEmailHTML({
                        name: emailModal.customer.fullName,
                        topDrink: getTopDrink(emailModal.customer.orders),
                        recencyDays: emailModal.customer.recencyDays,
                        frequency: emailModal.customer.frequency,
                        totalSpent: emailModal.customer.totalSpent,
                    }),
                }]

            const res = await fetch(`${BASE_URL}/api/notify-inactive-customers`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ customers }),
            })
            const data = await res.json()
            setEmailResults(data.results ?? [])
            setEmailDone(true)
        } catch (err) {
            setEmailResults([{ name: "Error", email: "", status: "failed" }])
            setEmailDone(true)
        } finally {
            setEmailLoading(false)
        }
    }

    const { atRisk, churnDist, summary } = useMemo(() => {
        if (!profiles) return { atRisk: [], churnDist: [], summary: null }
        const withOrders = profiles.filter((p) => p.frequency > 0)
        const atRisk = withOrders
            .filter((p) => p.churnScore >= 50)
            .sort((a, b) => b.churnScore - a.churnScore)
        const buckets = [
            { label: "0–25 (Healthy)", min: 0, max: 25, color: C.green },
            { label: "26–50 (Low Risk)", min: 26, max: 50, color: C.teal },
            { label: "51–70 (Medium)", min: 51, max: 70, color: C.amber },
            { label: "71–100 (High Risk)", min: 71, max: 100, color: C.coral },
        ]
        const churnDist = buckets.map((b) => ({
            ...b,
            count: withOrders.filter((p) => p.churnScore >= b.min && p.churnScore <= b.max).length,
        }))
        const highRisk = withOrders.filter((p) => p.churnScore >= 70).length
        const avgScore = withOrders.length
            ? Math.round(withOrders.reduce((s, p) => s + p.churnScore, 0) / withOrders.length)
            : 0
        return { atRisk, churnDist, summary: { highRisk, avgScore, total: withOrders.length } }
    }, [profiles])

    if (loading) return <Card><SectionTitle title="Churn Detection" /><Spinner height={300} /></Card>

    return (
        <>
            <Card>
                <SectionTitle title="Customer Churn Detection" subtitle="Risk scoring based on recency & purchase frequency" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                    <KPI label="High-Risk Customers" value={summary?.highRisk ?? "—"} sub="score ≥ 70" color={C.coral} />
                    <KPI label="Avg Churn Score" value={`${summary?.avgScore ?? "—"}/100`} sub="lower is healthier" color={C.amber} />
                    <KPI label="Customers Tracked" value={summary?.total?.toLocaleString() ?? "—"} sub="with purchase history" color={C.blue} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ flex: 1, minHeight: 0 }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Churn Risk Distribution</p>
                        <div style={{ flex: 1, minHeight: 220 }}>
                            <ResponsiveContainer debounce={200} width="100%" height={220}>
                                <BarChart data={churnDist} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" name="Customers" radius={[4, 4, 0, 0]}>
                                        {churnDist.map((d) => <Cell key={d.label} fill={d.color} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>
                            Top At-Risk Customers (Score ≥ 50)
                        </p>
                        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }} className="flex flex-col gap-1.5">
                            {atRisk.length === 0 ? (
                                <p className="text-xs" style={{ color: C.muted }}>No high-risk customers. 🎉</p>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setEmailModal({ bulk: true, customers: atRisk })}
                                        style={{
                                            width: "100%", padding: "7px 12px", borderRadius: 8,
                                            border: `1px solid ${C.amber}55`, background: C.amber + "10",
                                            color: C.amber, fontSize: 11, fontWeight: 600,
                                            cursor: "pointer", display: "flex", alignItems: "center",
                                            justifyContent: "center", gap: 6, marginBottom: 8,
                                        }}>
                                        <EmailIcon width={12} height={12} stroke={C.amber} />
                                        Send Re-engagement Email to All ({atRisk.length})
                                    </button>
                                    {atRisk
                                        .slice(atRiskPage * AT_RISK_PAGE_SIZE, (atRiskPage + 1) * AT_RISK_PAGE_SIZE)
                                        .map((p) => {
                                        const drink = getTopDrink(p.orders)
                                        return (
                                            <div key={p.user_id}
                                                className="flex items-center justify-between rounded-lg px-3 py-2 border"
                                                style={{ borderColor: C.border, background: C.dark }}>
                                                <div>
                                                    <p className="m-0 text-xs font-semibold" style={{ color: C.text }}>{p.fullName}</p>
                                                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                                        Last seen {p.recencyDays}d ago · {p.frequency} orders
                                                        {drink && (
                                                            <span style={{ color: C.amber }}> · ☕ {drink}</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="text-right">
                                                        <p className="m-0 text-xs font-bold"
                                                            style={{ color: p.churnScore >= 70 ? C.coral : C.amber }}>
                                                            {p.churnScore}
                                                        </p>
                                                        <p className="m-0 text-[9px]" style={{ color: C.muted }}>score</p>
                                                    </div>
                                                    <ChurnBadge score={p.churnScore} />
                                                    <button
                                                        onClick={() => setEmailModal({ customer: p })}
                                                        title="Send re-engagement email"
                                                        style={{
                                                            background: "none", border: `1px solid ${C.border}`,
                                                            borderRadius: 6, padding: "3px 7px", cursor: "pointer",
                                                            color: C.muted, display: "flex", alignItems: "center",
                                                        }}>
                                                        <EmailIcon width={11} height={11} />
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {atRisk.length > AT_RISK_PAGE_SIZE && (
                                        <div className="flex items-center justify-between mt-1">
                                            <span className="text-[10px]" style={{ color: C.muted }}>
                                                Page {atRiskPage + 1} of {Math.ceil(atRisk.length / AT_RISK_PAGE_SIZE)}
                                            </span>
                                            <div className="flex gap-1.5">
                                                <button disabled={atRiskPage === 0}
                                                    onClick={() => setAtRiskPage(p => p - 1)}
                                                    className="px-2.5 py-1 rounded-lg text-[10px] border disabled:opacity-40"
                                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>
                                                    ← Prev
                                                </button>
                                                <button disabled={atRiskPage >= Math.ceil(atRisk.length / AT_RISK_PAGE_SIZE) - 1}
                                                    onClick={() => setAtRiskPage(p => p + 1)}
                                                    className="px-2.5 py-1 rounded-lg text-[10px] border disabled:opacity-40"
                                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>
                                                    Next →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            {/* ══════════════════════════════════════════
                EMAIL MODAL — redesigned with preview iframe
            ══════════════════════════════════════════ */}
            {emailModal && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 1100,
                    background: "rgba(17,10,0,0.60)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 20,
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: 20,
                        width: "100%",
                        maxWidth: 620,
                        maxHeight: "92vh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        boxShadow: "0 28px 80px rgba(28,10,0,0.28), 0 0 0 1px rgba(217,119,6,0.15)",
                    }}>

                        {/* ── Modal header ── */}
                        <div style={{
                            padding: "0",
                            background: "#1C0A00",
                            flexShrink: 0,
                        }}>
                            {/* Gold top bar */}
                            <div style={{
                                height: 3,
                                background: "linear-gradient(90deg,#7C2D00,#C05D00,#D97706,#C05D00,#7C2D00)",
                            }} />
                            <div style={{
                                padding: "14px 20px",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                                    <div style={{
                                        width: 38, height: 38, borderRadius: "50%",
                                        background: "#D97706",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 18, flexShrink: 0,
                                        boxShadow: "0 3px 10px rgba(217,119,6,0.4)",
                                    }}>☕</div>
                                    <div>
                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#FDF6EE", lineHeight: 1.3 }}>
                                            {emailModal.bulk
                                                ? `Re-engagement Campaign — ${emailModal.customers.length} Customers`
                                                : `Re-engagement Email — ${emailModal.customer.fullName}`}
                                        </p>
                                        <p style={{ margin: 0, fontSize: 10.5, color: "#8B6347", lineHeight: 1.4 }}>
                                            {emailModal.bulk
                                                ? "Each email is personalized with the customer's top drink"
                                                : (() => {
                                                    const drink = getTopDrink(emailModal.customer.orders)
                                                    return `${emailModal.customer.email} · ${emailModal.customer.recencyDays}d inactive${drink ? ` · ☕ ${drink}` : ""}`
                                                })()}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={closeModal} style={{
                                    background: "rgba(255,255,255,0.1)",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    borderRadius: 8, width: 30, height: 30,
                                    cursor: "pointer", color: "#8B6347",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0,
                                }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* ── Tabs (hidden when loading / done) ── */}
                        {!emailDone && !emailLoading && (
                            <div style={{
                                display: "flex",
                                borderBottom: `1px solid ${C.border}`,
                                background: C.dark,
                                flexShrink: 0,
                            }}>
                                {[
                                    { id: "preview", label: "✉️  Email Preview" },
                                    ...(emailModal.bulk
                                        ? [{ id: "recipients", label: `👥  Recipients (${emailModal.customers.length})` }]
                                        : []),
                                ].map(tab => (
                                    <button key={tab.id} onClick={() => setModalTab(tab.id)} style={{
                                        flex: 1, padding: "9px 0",
                                        background: "none", border: "none",
                                        borderBottom: modalTab === tab.id
                                            ? `2px solid #D97706`
                                            : "2px solid transparent",
                                        color: modalTab === tab.id ? "#D97706" : C.muted,
                                        fontSize: 11, fontWeight: modalTab === tab.id ? 700 : 400,
                                        cursor: "pointer", marginBottom: -1, letterSpacing: "0.01em",
                                    }}>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* ── Modal body ── */}
                        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

                            {/* LOADING STATE */}
                            {emailLoading && (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", gap: 14, padding: "56px 20px",
                                }}>
                                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 34, height: 34 }}>
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                                        <path fill="#D97706" d="M4 12a8 8 0 018-8v8z"/>
                                    </svg>
                                    <p style={{ margin: 0, fontSize: 13, color: C.muted, fontWeight: 500 }}>
                                        Sending personalized emails via Brevo…
                                    </p>
                                </div>
                            )}

                            {/* DONE STATE */}
                            {emailDone && (
                                <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "12px 16px", borderRadius: 12,
                                        background: C.green + "0C", border: `1px solid ${C.green}33`,
                                        marginBottom: 6,
                                    }}>
                                        <span style={{ fontSize: 22 }}>✅</span>
                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>
                                            {emailResults.filter(r => r.status === "sent").length} sent
                                            {emailResults.filter(r => r.status === "failed").length > 0 &&
                                                ` · ${emailResults.filter(r => r.status === "failed").length} failed`}
                                        </p>
                                    </div>
                                    {emailResults.map((r, i) => (
                                        <div key={i} style={{
                                            display: "flex", justifyContent: "space-between", alignItems: "center",
                                            padding: "8px 14px", borderRadius: 10,
                                            border: `1px solid ${r.status === "sent" ? C.green + "44" : C.coral + "44"}`,
                                            background: r.status === "sent" ? C.green + "06" : C.coral + "06",
                                            fontSize: 12,
                                        }}>
                                            <div>
                                                <span style={{ fontWeight: 600, color: C.text }}>{r.name}</span>
                                                <span style={{ color: C.muted, marginLeft: 8, fontSize: 11 }}>{r.email}</span>
                                            </div>
                                            <span style={{
                                                color: r.status === "sent" ? C.green : C.coral,
                                                fontWeight: 700, fontSize: 11,
                                            }}>
                                                {r.status === "sent" ? "✓ Sent" : "✗ Failed"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* PREVIEW TAB */}
                            {!emailDone && !emailLoading && modalTab === "preview" && (
                                <div>
                                    {/* Bulk customer switcher */}
                                    {emailModal.bulk && (
                                        <div style={{
                                            padding: "10px 16px 8px",
                                            borderBottom: `1px solid ${C.border}`,
                                            display: "flex", gap: 6, flexWrap: "wrap",
                                            alignItems: "center", background: C.dark,
                                        }}>
                                            <span style={{
                                                fontSize: 10, color: C.muted,
                                                fontWeight: 600, textTransform: "uppercase",
                                                letterSpacing: "0.1em", marginRight: 2,
                                            }}>Preview for:</span>
                                            {emailModal.customers.map(c => (
                                                <button key={c.user_id}
                                                    onClick={() => setPreviewCustomer(c)}
                                                    style={{
                                                        padding: "3px 11px", borderRadius: 50, fontSize: 10, fontWeight: 600,
                                                        border: `1px solid ${previewCustomer?.user_id === c.user_id ? "#D97706" : C.border}`,
                                                        background: previewCustomer?.user_id === c.user_id ? "#D97706" + "18" : "#fff",
                                                        color: previewCustomer?.user_id === c.user_id ? "#D97706" : C.textSoft,
                                                        cursor: "pointer", transition: "all 0.12s",
                                                    }}>
                                                    {c.first_name ?? c.fullName.split(" ")[0]}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Top drink callout bar */}
                                    {previewCustomer && getTopDrink(previewCustomer.orders) && (
                                        <div style={{
                                            display: "flex", alignItems: "center", gap: 9,
                                            padding: "9px 16px",
                                            background: "#D97706" + "0C",
                                            borderBottom: `1px solid #D97706" + "22`,
                                            fontSize: 11.5,
                                        }}>
                                            <span style={{ fontSize: 16 }}>☕</span>
                                            <span style={{ color: C.textSoft }}>
                                                Top drink for{" "}
                                                <strong style={{ color: C.text }}>
                                                    {previewCustomer.fullName}
                                                </strong>:{" "}
                                                <strong style={{ color: "#D97706" }}>
                                                    {getTopDrink(previewCustomer.orders)}
                                                </strong>
                                                {" "}— featured in the email below
                                            </span>
                                        </div>
                                    )}

                                    {/* iFrame preview */}
                                    <div style={{ padding: "14px 16px 16px" }}>
                                        <EmailPreview customer={previewCustomer} />
                                    </div>
                                </div>
                            )}

                            {/* RECIPIENTS TAB */}
                            {!emailDone && !emailLoading && modalTab === "recipients" && emailModal.bulk && (
                                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
                                    {emailModal.customers.map(c => {
                                        const drink = getTopDrink(c.orders)
                                        return (
                                            <div key={c.user_id} style={{
                                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                                padding: "9px 14px", borderRadius: 10,
                                                border: `1px solid ${C.border}`, background: C.dark, fontSize: 12,
                                            }}>
                                                <div>
                                                    <p style={{ margin: 0, fontWeight: 600, color: C.text }}>{c.fullName}</p>
                                                    <p style={{ margin: "2px 0 0", fontSize: 10.5, color: C.muted }}>
                                                        {c.email} · {c.recencyDays}d inactive · score {c.churnScore}
                                                    </p>
                                                </div>
                                                {drink ? (
                                                    <span style={{
                                                        fontSize: 10.5, padding: "4px 10px", borderRadius: 50,
                                                        background: "#D97706" + "14", border: `1px solid #D97706" + "33`,
                                                        color: "#D97706", fontWeight: 600, whiteSpace: "nowrap",
                                                        marginLeft: 12,
                                                    }}>
                                                        ☕ {drink}
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        fontSize: 10.5, padding: "4px 10px", borderRadius: 50,
                                                        background: C.border, color: C.muted, marginLeft: 12,
                                                    }}>No drink data</span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ── Footer buttons ── */}
                        {!emailDone ? (
                            <div style={{
                                padding: "12px 20px",
                                borderTop: `1px solid ${C.border}`,
                                display: "flex", gap: 8,
                                background: C.dark, flexShrink: 0,
                            }}>
                                <button onClick={closeModal} style={{
                                    flex: 1, padding: "10px 0", borderRadius: 10,
                                    border: `1px solid ${C.border}`, background: "#fff",
                                    color: C.textSoft, fontSize: 12, fontWeight: 500, cursor: "pointer",
                                }}>Cancel</button>
                                <button disabled={emailLoading} onClick={handleSendEmails} style={{
                                    flex: 2, padding: "10px 0", borderRadius: 10, border: "none",
                                    background: emailLoading ? C.border : "#1C0A00",
                                    color: "#FDF6EE", fontSize: 12, fontWeight: 700,
                                    cursor: emailLoading ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    gap: 8, letterSpacing: "0.04em",
                                    boxShadow: emailLoading ? "none" : "0 3px 10px rgba(28,10,0,0.25)",
                                }}>
                                    <EmailIcon width={13} height={13} stroke="#FDF6EE" />
                                    {emailLoading
                                        ? "Sending…"
                                        : emailModal.bulk
                                            ? `Send ${emailModal.customers.length} Personalized Emails`
                                            : "Send Email"}
                                </button>
                            </div>
                        ) : (
                            <div style={{
                                padding: "12px 20px",
                                borderTop: `1px solid ${C.border}`,
                                flexShrink: 0,
                            }}>
                                <button onClick={closeModal} style={{
                                    width: "100%", padding: "10px 0", borderRadius: 10,
                                    border: "none", background: C.green,
                                    color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                    boxShadow: "0 3px 10px rgba(22,163,74,0.28)",
                                }}>Done</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

const CustomerLTV = ({ profiles, loading }) => {
    const [topCustomersPage, setTopCustomersPage] = useState(0)
    const TOP_CUSTOMERS_PAGE_SIZE = 8
    const { ltvSegments, topCustomers, kpis } = useMemo(() => {
        if (!profiles) return { ltvSegments: [], topCustomers: [], kpis: null }

        const withOrders = profiles.filter((p) => p.frequency > 0)

        const sorted = [...withOrders].sort((a, b) => a.projectedLTV - b.projectedLTV)
        const q2 = sorted[Math.floor(sorted.length * 0.5)]?.projectedLTV ?? 0
        const q3 = sorted[Math.floor(sorted.length * 0.75)]?.projectedLTV ?? 0

        const ltvSegments = [
            { label: "Low (Q1)", range: `< ${fmt(q2)}`, count: withOrders.filter(p => p.projectedLTV < q2).length, color: C.muted },
            { label: "Mid (Q2–3)", range: `${fmt(q2)}–${fmt(q3)}`, count: withOrders.filter(p => p.projectedLTV >= q2 && p.projectedLTV < q3).length, color: C.blue },
            { label: "High (Q3+)", range: `> ${fmt(q3)}`, count: withOrders.filter(p => p.projectedLTV >= q3).length, color: C.amber },
        ]

        const topCustomers = [...withOrders]
            .sort((a, b) => b.projectedLTV - a.projectedLTV)

        const avgLTV = withOrders.length
            ? withOrders.reduce((s, p) => s + p.projectedLTV, 0) / withOrders.length
            : 0
        const totalLTV = withOrders.reduce((s, p) => s + p.projectedLTV, 0)
        const topLTV = withOrders.reduce((best, p) => p.projectedLTV > best.projectedLTV ? p : best, withOrders[0] ?? {})

        return {
            ltvSegments,
            topCustomers,
            kpis: { avgLTV, totalLTV, topCustomer: topLTV?.fullName, topLTVVal: topLTV?.projectedLTV ?? 0 },
        }
    }, [profiles])

    if (loading) return <Card><SectionTitle title="Customer Lifetime Value" /><Spinner height={300} /></Card>

    return (
        <Card>
            <SectionTitle title="Customer Lifetime Value Tracking"
                subtitle="Projected annual LTV based on each customer's purchase velocity" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                <KPI label="Avg LTV (Projected)" value={kpis ? fmt(kpis.avgLTV) : "—"} color={C.teal} />
                <KPI label="Total Portfolio LTV" value={kpis ? fmt(kpis.totalLTV) : "—"} color={C.green} />
                <KPI label="Highest LTV Customer" value={kpis?.topCustomer ?? "—"}
                    sub={kpis ? fmt(kpis.topLTVVal) : ""} color={C.amber} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>LTV Segments</p>
                    <div className="flex flex-col gap-2">
                        {ltvSegments.map((s) => (
                            <div key={s.label} className="rounded-xl p-3 border"
                                style={{ borderColor: C.border, background: C.dark }}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-semibold text-xs" style={{ color: s.color }}>{s.label}</span>
                                    <span className="font-bold text-sm" style={{ color: C.text }}>{s.count}</span>
                                </div>
                                <p className="m-0 text-[10px]" style={{ color: C.muted }}>{s.range}</p>
                                <div className="mt-1.5 h-1 rounded-full" style={{ background: C.border }}>
                                    <div className="h-1 rounded-full" style={{
                                        width: `${Math.round((s.count / (profiles?.filter(p => p.frequency > 0).length || 1)) * 100)}%`,
                                        background: s.color,
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Top Customers by LTV</p>
                    <div className="flex flex-col gap-1.5">
                        {topCustomers
                            .slice(topCustomersPage * TOP_CUSTOMERS_PAGE_SIZE, (topCustomersPage + 1) * TOP_CUSTOMERS_PAGE_SIZE)
                            .map((p, i) => {
                            const rank = topCustomersPage * TOP_CUSTOMERS_PAGE_SIZE + i
                            return (
                            <div key={p.user_id}
                                className="flex items-center justify-between rounded-lg px-3 py-2 border"
                                style={{ borderColor: C.border, background: C.dark }}>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold w-4 text-center"
                                        style={{ color: rank === 0 ? C.amber : C.muted }}>
                                        {rank === 0 ? "★" : `${rank + 1}`}
                                    </span>
                                    <div>
                                        <p className="m-0 text-xs font-semibold" style={{ color: C.text }}>{p.fullName}</p>
                                        <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                            {p.frequency} orders · <StageChip stage={p.stage} />
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="m-0 text-xs font-bold" style={{ color: C.amber }}>{fmt(p.projectedLTV)}</p>
                                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>proj. annual</p>
                                </div>
                            </div>
                            )
                        })}
                    </div>
                    {topCustomers.length > TOP_CUSTOMERS_PAGE_SIZE && (
                        <div className="flex items-center justify-between mt-2">
                            <span className="text-[10px]" style={{ color: C.muted }}>
                                Page {topCustomersPage + 1} of {Math.ceil(topCustomers.length / TOP_CUSTOMERS_PAGE_SIZE)}
                            </span>
                            <div className="flex gap-1.5">
                                <button disabled={topCustomersPage === 0}
                                    onClick={() => setTopCustomersPage(p => p - 1)}
                                    className="px-2.5 py-1 rounded-lg text-[10px] border disabled:opacity-40"
                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>
                                    ← Prev
                                </button>
                                <button disabled={topCustomersPage >= Math.ceil(topCustomers.length / TOP_CUSTOMERS_PAGE_SIZE) - 1}
                                    onClick={() => setTopCustomersPage(p => p + 1)}
                                    className="px-2.5 py-1 rounded-lg text-[10px] border disabled:opacity-40"
                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: "pointer" }}>
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    )
}

const parseAISections = (text) => {
    if (!text) return []
    const lines = text.split('\n')
    const sections = []
    let current = null

    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        if (trimmed.startsWith('### ')) {
            if (current) sections.push(current)
            current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
        } else if (/^[-•*]\s+/.test(trimmed)) {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
        } else {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed)
        }
    }
    if (current) sections.push(current)
    return sections
}

const SECTION_COLORS = {
    'WHO ARE OUR CUSTOMERS RIGHT NOW?': { color: C.purple, icon: '👥' },
    'WHAT LIFECYCLE STAGE ARE THEY IN?': { color: C.teal,   icon: '🔄' },
    'HOW MANY ARE WE AT RISK OF LOSING?': { color: C.coral,  icon: '📉' },
    'WHAT IS THE CUSTOMER BASE WORTH?': { color: C.green,  icon: '💰' },
    'WHEN AND HOW ARE CUSTOMERS BUYING?': { color: C.blue,   icon: '📊' },
}

const DEFAULT_SECTION_COLORS = [C.purple, C.teal, C.amber, C.blue, C.coral, C.green]

const renderBulletText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
            : part
    )
}

const MarkdownBlock = ({ text }) => {
    if (!text) return null
    const sections = parseAISections(text)
    if (!sections.length) return null

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sections.map((section, idx) => {
                const key = section.title?.toUpperCase().trim()
                const cfg = (key && SECTION_COLORS[key]) || {
                    color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
                    icon: '•',
                }
                return (
                    <div key={idx} style={{ borderRadius: 10, border: `1px solid ${cfg.color}28`, overflow: 'hidden' }}>
                        {section.title && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 12px',
                                background: cfg.color + '10',
                                borderBottom: `1px solid ${cfg.color}20`,
                            }}>
                                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: cfg.color }}>
                                    {section.title}
                                </span>
                            </div>
                        )}
                        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {section.bullets.map((bullet, bIdx) => (
                                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 7 }} />
                                    <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>
                                        {renderBulletText(bullet)}
                                    </span>
                                </div>
                            ))}
                            {section.bullets.length === 0 && (
                                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{section.title}</span>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default function CustomerIntelligenceDashboard({ onGoToProfile }) {
    const { customers, orders } = useRawData()
    const loading = customers === null || orders === null
    const profiles = useMemo(() => buildCustomerProfiles(customers, orders), [customers, orders])
    const [aiOpen, setAiOpen] = useState(false)
    const [aiSummary, setAiSummary] = useState(() => sessionStorage.getItem('ci_aiSummary') || null)
    const [aiOutlook, setAiOutlook] = useState(() => sessionStorage.getItem('ci_aiOutlook') || null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [aiGeneratedAt, setAiGeneratedAt] = useState(null)
    const [aiGeneratedFor, setAiGeneratedFor] = useState(null)
    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useAIHistory('customer-intelligence-history')
    const { history: profilingHistory, loading: profilingHistoryLoading, saveProfilingRun, deleteProfilingRun } = useProfilingHistory()
    const [expandedProfilingId, setExpandedProfilingId] = useState(null)
    const [deletingProfilingId, setDeletingProfilingId] = useState(null)
    const [loadedProfilingId, setLoadedProfilingId] = useState(null)
    const [loadedRunInfo, setLoadedRunInfo] = useState(null)
    const [aiTab, setAiTab] = useState("analysis")
    const [expandedHistoryId, setExpandedHistoryId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)
    const [profilesCache, setProfilesCache] = useState({})
    const [profilingLoading, setProfilingLoading] = useState(false)
    const [latestDigest, setLatestDigest] = useState(null)
    const [digestLoading, setDigestLoading] = useState(false)

    useEffect(() => {
        setDigestLoading(true)
        fetch(`${BASE_URL}/api/digests/latest`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => {
                if (data && data.briefing) setLatestDigest(data)
            })
            .catch(console.error)
            .finally(() => setDigestLoading(false))
    }, [])

    const handleGenerateInsights = async () => {
        if (!profiles) return
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiOutlook(null)

        try {
            const withOrders = profiles.filter(p => p.frequency > 0)
            const stageCounts = Object.entries(
                profiles.reduce((acc, p) => {
                    if (!acc[p.stage]) acc[p.stage] = { stage: p.stage, count: 0, revenue: 0 }
                    acc[p.stage].count += 1
                    acc[p.stage].revenue += p.totalSpent
                    return acc
                }, {})
            ).map(([, v]) => ({ ...v, pct: ((v.count / profiles.length) * 100).toFixed(1) }))

            const avgLTV = withOrders.length
                ? withOrders.reduce((s, p) => s + p.projectedLTV, 0) / withOrders.length
                : 0
            const totalPortfolioLTV = withOrders.reduce((s, p) => s + p.projectedLTV, 0)
            const topCustomer = [...withOrders].sort((a, b) => b.projectedLTV - a.projectedLTV)[0]?.fullName ?? '—'
            const highRisk = withOrders.filter(p => p.churnScore >= 70).length
            const avgScore = withOrders.length
                ? Math.round(withOrders.reduce((s, p) => s + p.churnScore, 0) / withOrders.length)
                : 0

            const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]
            const dayMap = {}, sourceMap = {}
            let totalRevenue = 0
            for (const o of (orders ?? [])) {
                const day = DAYS[toPHT(o.created_at).getUTCDay()]
                dayMap[day] = (dayMap[day] || 0) + 1
                const src = o.order_source ?? 'pos'
                if (!sourceMap[src]) sourceMap[src] = 0
                sourceMap[src] += 1
                totalRevenue += Number(o.final_amount)
            }
            const peakDay = Object.entries(dayMap).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—'
            const totalOrders = orders?.length ?? 0
            const sources = Object.entries(sourceMap).map(([source, count]) => ({
                source, orders: count,
                pct: ((count / (totalOrders || 1)) * 100).toFixed(1)
            }))

            const res = await fetch(`${BASE_URL}/api/summarize-customers`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    totalCustomers: profiles.length,
                    totalWithOrders: withOrders.length,
                    avgLTV,
                    totalPortfolioLTV,
                    topCustomer,
                    stageCounts,
                    churnSummary: { highRisk, avgScore, total: withOrders.length },
                    purchasePatterns: { peakDay, peakMonth: '—', totalOrders, totalRevenue, sources },
                })
            })
            if (!res.ok) throw new Error('Failed to generate insights')
            const { summary, outlook } = await res.json()

            setAiSummary(summary)
            setAiOutlook(outlook)
            setAiGeneratedAt(new Date())
            setAiGeneratedFor({ customers: profiles.length, withOrders: withOrders.length })
            sessionStorage.setItem('aiSummary', summary)
            sessionStorage.setItem('aiOutlook', outlook)
            await saveAnalysis({
                summary,
                recommendations: outlook,
                branchName: "All Customers",
                dateRange: `${profiles.length} customers · ${withOrders.length} with orders`,
                startISO: null,
                endISO: null,
            })
        } catch (err) {
            setAiError(err.message || 'Failed to generate analysis.')
        } finally {
            setAiLoading(false)
        }
    }

    const handleGenerateProfiles = async () => {
       if (!profiles) return
        const toProfile = profiles
            .filter(p => p.frequency > 0)
            .map(p => ({
                user_id: p.user_id,
                fullName: p.fullName,
                email: p.email,
                recencyDays: p.recencyDays,
                frequency: p.frequency,
                totalSpent: p.totalSpent,
                orders: p.orders,
                stage: p.stage,
                churnScore: p.churnScore,
            }))

        setProfilingLoading(true)
        setLoadedProfilingId(null)
        setLoadedRunInfo(null)
        try {
            const res = await fetch(`${BASE_URL}/api/agent/profile-customers`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ customers: toProfile }),
            })
            const data = await res.json()
            const cache = {}
            data.profiles?.forEach(prof => {
                if (prof.userId) cache[prof.userId] = prof
            })
            setProfilesCache(cache)

            if (data.profiles?.length) {
                await saveProfilingRun(data.profiles)
            }
        } catch (err) {
            console.error('Profiling failed:', err)
        } finally {
            setProfilingLoading(false)
        }
    }

    const loadProfilingRun = (entry) => {
        const cache = {}
        entry.profiles?.forEach(p => { if (p.userId) cache[p.userId] = p })
        setProfilesCache(cache)
        setLoadedProfilingId(entry.id)
        const matched = profiles ? profiles.filter(p => cache[p.user_id]).length : Object.keys(cache).length
        setLoadedRunInfo({ id: entry.id, matched, total: Object.keys(cache).length, generatedAt: entry.generated_at })
    }

    return (
        <div className="p-6 font-sans" style={{ background: C.espresso }}>
            <div className="flex flex-col gap-5">
                {/* ═══ WEEKLY DIGEST CARD ═══ */}
                {latestDigest && (
                    <Card>
                        <SectionTitle title="📅 Weekly Manager Briefing" subtitle={`${latestDigest.week_start} to ${latestDigest.week_end}`} />
                        {digestLoading ? (
                            <Spinner height={100} />
                        ) : (
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7, color: C.text }}>
                                {latestDigest.briefing}
                            </div>
                        )}
                        <div style={{ marginTop: 12, fontSize: 10, color: C.muted }}>
                            Generated {new Date(latestDigest.generated_at).toLocaleString('en-PH')}
                        </div>
                    </Card>
                )}

                {/* ═══ GENERATE AI PROFILES BUTTON ═══ */}
                <div className="flex justify-end">
                    <button
                        onClick={() => {
                            setAiOpen(true)
                            setAiTab('profiling')
                            handleGenerateProfiles()
                        }}
                        disabled={profilingLoading || loading}
                        style={{
                            background: profilingLoading ? C.border : C.purple,
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: '8px 16px', fontWeight: 600, fontSize: 12,
                            cursor: profilingLoading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                        {profilingLoading ? (
                            <>
                                <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                    <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Generating Profiles…
                            </>
                        ) : (
                            <>✦ Generate AI Profiles</>
                        )}
                    </button>
                </div>

                <CustomerHistoryTable profiles={profiles} loading={loading} profilesCache={profilesCache} />
                <PurchasePatternAnalysis orders={orders} loading={loading} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ alignItems: "stretch" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <LifecycleClassification profiles={profiles} loading={loading} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <ChurnDetection profiles={profiles} loading={loading} />
                    </div>
                </div>
                <CustomerLTV profiles={profiles} loading={loading} />
            </div>

            {/* AI FAB */}
            <button
                onClick={() => setAiOpen(true)}
                title="Open AI Analyst"
                style={{
                    position: "fixed", bottom: 28, right: 28,
                    width: 52, height: 52, borderRadius: "50%",
                    background: C.purple, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 18px rgba(124,58,237,0.35)", zIndex: 998,
                }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                </svg>
                {aiSummary && (
                    <span style={{
                        position: "absolute", top: 2, right: 2,
                        width: 10, height: 10, borderRadius: "50%",
                        background: C.amber, border: "2px solid #fff",
                    }} />
                )}
            </button>

            {/* AI Sidebar backdrop */}
            {aiOpen && (
                <div onClick={() => setAiOpen(false)} style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.18)",
                    zIndex: 999,
                }} />
            )}

            {/* AI Sidebar */}
            <div style={{
                position: "fixed", top: 0, right: 0, bottom: 0,
                width: "min(420px, 100vw)",
                background: "#fff", borderLeft: `1px solid ${C.border}`, zIndex: 1000,
                display: "flex", flexDirection: "column",
                transform: aiOpen ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: aiOpen ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: C.purple + "06", flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%",
                            background: C.purple, display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                            </svg>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>AI Customer Analyst</p>
                            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Interpretation · Churn Signals · Outlook</p>
                        </div>
                    </div>
                    <button onClick={() => setAiOpen(false)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: C.muted }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {/* Tab switcher */}
                <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.dark }}>
                    {[
                        { id: "analysis", label: "Analysis" },
                        { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` },
                         { id: "profiling", label: `Profiles${profilingHistory?.length ? ` (${profilingHistory.length})` : ""}` },
                    ].map(tab => (
                        <button key={tab.id} onClick={() => setAiTab(tab.id)}
                            style={{
                                flex: 1, padding: "10px 0",
                                background: "none", border: "none",
                                borderBottom: aiTab === tab.id ? `2px solid ${C.purple}` : "2px solid transparent",
                                color: aiTab === tab.id ? C.purple : C.muted,
                                fontSize: 12, fontWeight: aiTab === tab.id ? 700 : 400,
                                cursor: "pointer", transition: "all 0.15s", marginBottom: -1,
                            }}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Sidebar content */}
                <div style={{ flex: 1, overflowY: "auto", padding: 20, minHeight: 0 }}>

                    {/* ── ANALYSIS TAB ── */}
                    {aiTab === "analysis" && (
                        <>
                            <button
                                onClick={handleGenerateInsights}
                                disabled={aiLoading || loading}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 50, border: "none",
                                    background: aiLoading || loading ? C.border : C.purple,
                                    color: "#fff", fontSize: 12, fontWeight: 600,
                                    cursor: aiLoading || loading ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    gap: 7, marginBottom: aiSummary ? 8 : 20,
                                    transition: "background 0.2s",
                                }}>
                                {aiLoading ? (
                                    <>
                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                            <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Analyzing your customers…
                                    </>
                                ) : aiSummary ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.62"/>
                                        </svg>
                                        Regenerate Analysis
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                        Generate Analysis
                                    </>
                                )}
                            </button>

                            {aiSummary && !aiLoading && aiGeneratedAt && (
                                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                                    {["🤖 Gemini 2.5 Flash", "👥 RFM-based profiles", "⚠️ For reference only"].map(label => (
                                        <span key={label} style={{
                                            fontSize: 9, padding: "2px 8px", borderRadius: 50,
                                            border: `1px solid ${C.border}`, color: C.muted, background: C.dark,
                                        }}>{label}</span>
                                    ))}
                                </div>
                            )}

                            <div style={{
                                padding: "10px 14px", borderRadius: 10,
                                background: C.dark, border: `1px solid ${C.border}`,
                                display: "flex", gap: 8, alignItems: "flex-start",
                                marginBottom: 16,
                            }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                                    <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                                    Customer insights are generated by Google Gemini 2.5 Flash using
                                    RFM-based profiles derived from your order data. Lifecycle stages,
                                    churn scores, and LTV projections are model-based estimates — not
                                    guaranteed outcomes. Use as decision-support only.
                                </p>
                            </div>

                            {aiError && (
                                <div style={{
                                    background: C.coral + "15", border: `1px solid ${C.coral}44`,
                                    borderRadius: 10, padding: "10px 14px",
                                    fontSize: 12, color: C.coral, marginBottom: 16,
                                }}>
                                    {aiError}
                                </div>
                            )}

                            {!aiSummary && !aiLoading && !aiError && (
                                <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted, fontSize: 12 }}>
                                    <div style={{
                                        width: 56, height: 56, borderRadius: "50%",
                                        background: C.purple + "12", margin: "0 auto 16px",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                                            <path fill={C.purple} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                    </div>
                                    <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>
                                        Customer Intelligence Analysis
                                    </p>
                                    <p style={{ margin: "0 0 16px", fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                        Click <strong>Generate Analysis</strong> to get an AI interpretation of your customer data.
                                    </p>
                                    {[
                                        { icon: "👥", label: "Customer base breakdown" },
                                        { icon: "🔄", label: "Lifecycle stage health" },
                                        { icon: "📉", label: "Churn risk signals" },
                                        { icon: "💰", label: "LTV outlook" },
                                    ].map(item => (
                                        <div key={item.label} style={{
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "8px 12px", borderRadius: 8, marginBottom: 6,
                                            background: C.dark, border: `1px solid ${C.border}`, textAlign: "left",
                                        }}>
                                            <span style={{ fontSize: 14 }}>{item.icon}</span>
                                            <span style={{ fontSize: 12, color: C.textSoft }}>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {aiLoading && (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 20px" }}>
                                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                        <path fill={C.purple} d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                    <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your customer data…</p>
                                </div>
                            )}

                            {aiSummary && !aiLoading && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div style={{ background: C.purple + "08", border: `1px solid ${C.purple}28`, borderRadius: 12, overflow: "hidden" }}>
                                        <div style={{
                                            padding: "10px 14px", borderBottom: `1px solid ${C.purple}18`,
                                            background: C.purple + "10", display: "flex", alignItems: "center", gap: 8,
                                        }}>
                                            <span style={{ fontSize: 14 }}>👥</span>
                                            <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.purple }}>
                                                Customer Intelligence
                                            </p>
                                        </div>
                                        <div style={{ padding: "12px 14px", fontSize: 13, lineHeight: 1.75, color: C.text, whiteSpace: "pre-wrap" }}>
                                            <MarkdownBlock text={aiSummary} />
                                        </div>
                                    </div>

                                    {aiOutlook && (
                                        <div style={{ background: C.teal + "08", border: `1px solid ${C.teal}28`, borderRadius: 12, overflow: "hidden" }}>
                                            <div style={{
                                                padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`,
                                                background: C.teal + "10", display: "flex", alignItems: "center", gap: 8,
                                            }}>
                                                <span style={{ fontSize: 14 }}>🔮</span>
                                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                                                    Signals & Outlook
                                                </p>
                                            </div>
                                            <div style={{ padding: "12px 14px", fontSize: 13, lineHeight: 1.75, color: C.text, whiteSpace: "pre-wrap" }}>
                                                <MarkdownBlock text={aiOutlook} />
                                            </div>
                                        </div>
                                    )}

                                    <div style={{
                                        padding: "10px 14px", borderRadius: 10,
                                        background: C.amber + "08", border: `1px solid ${C.amber}22`,
                                        display: "flex", gap: 8, alignItems: "flex-start",
                                    }}>
                                        <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
                                        <p style={{ margin: 0, fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
                                            This analysis reflects all customers currently loaded. Click <strong>Regenerate</strong> to refresh after data changes.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── HISTORY TAB ── */}
                    {aiTab === "history" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {historyLoading ? (
                                <Spinner height={160} />
                            ) : !history?.length ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", textAlign: "center" }}>
                                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.muted + "18", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>🕓</div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                        Generated analyses will be saved here automatically.
                                    </p>
                                </div>
                            ) : history.map((entry) => {
                                const isOpen = expandedHistoryId === entry.id
                                const generatedDate = new Date(entry.generated_at)
                                return (
                                    <div key={entry.id} style={{
                                        borderRadius: 12,
                                        border: `1px solid ${isOpen ? C.purple + "44" : C.border}`,
                                        overflow: "hidden", transition: "border-color 0.15s",
                                    }}>
                                        <div
                                            onClick={() => setExpandedHistoryId(isOpen ? null : entry.id)}
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                padding: "10px 14px",
                                                background: isOpen ? C.purple + "08" : C.dark,
                                                cursor: "pointer", gap: 10, transition: "background 0.15s",
                                            }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, background: C.purple + "15", borderRadius: 50, padding: "2px 8px" }}>
                                                        {entry.branch_name}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: C.textSoft, background: C.border + "80", borderRadius: 50, padding: "2px 8px" }}>
                                                        {entry.date_range}
                                                    </span>
                                                </div>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                                                    {" at "}
                                                    {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                                                </p>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation()
                                                        setDeletingId(entry.id)
                                                        await deleteAnalysis(entry.id)
                                                        if (expandedHistoryId === entry.id) setExpandedHistoryId(null)
                                                        setDeletingId(null)
                                                    }}
                                                    disabled={deletingId === entry.id}
                                                    title="Delete this entry"
                                                    style={{
                                                        background: "none", border: `1px solid ${C.border}`,
                                                        borderRadius: 6, padding: "3px 6px",
                                                        cursor: deletingId === entry.id ? "not-allowed" : "pointer",
                                                        color: C.muted, display: "flex", alignItems: "center",
                                                        opacity: deletingId === entry.id ? 0.5 : 1,
                                                    }}>
                                                    {deletingId === entry.id ? (
                                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                                                            <path fill={C.muted} d="M4 12a8 8 0 018-8v8z"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                                                            <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                        </svg>
                                                    )}
                                                </button>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                                    fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2"
                                                    style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
                                                    <path d="M6 9l6 6 6-6"/>
                                                </svg>
                                            </div>
                                        </div>

                                        {isOpen && (
                                            <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                                                <div style={{ background: C.purple + "06", borderRadius: 10, border: `1px solid ${C.purple}18`, overflow: "hidden" }}>
                                                    <div style={{ padding: "7px 12px", borderBottom: `1px solid ${C.purple}14`, background: C.purple + "0E", display: "flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ fontSize: 12 }}>👥</span>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                            Customer Intelligence
                                                        </span>
                                                    </div>
                                                    <div style={{ padding: "10px 12px", fontSize: 13, lineHeight: 1.75, color: C.text, whiteSpace: "pre-wrap" }}>
                                                        <MarkdownBlock text={entry.summary} />
                                                    </div>
                                                </div>

                                                {entry.recommendations && (
                                                    <div style={{ background: C.teal + "06", borderRadius: 10, border: `1px solid ${C.teal}18`, overflow: "hidden" }}>
                                                        <div style={{ padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`, background: C.teal + "0E", display: "flex", alignItems: "center", gap: 6 }}>
                                                            <span style={{ fontSize: 12 }}>🔮</span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                                Signals & Outlook
                                                            </span>
                                                        </div>
                                                        <div style={{ padding: "10px 12px", fontSize: 13, lineHeight: 1.75, color: C.text, whiteSpace: "pre-wrap" }}>
                                                            <MarkdownBlock text={entry.recommendations} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {aiTab === "profiling" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <button
                                onClick={handleGenerateProfiles}
                                disabled={profilingLoading || loading}
                                style={{
                                    background: profilingLoading ? C.border : C.purple,
                                    color: '#fff', border: 'none', borderRadius: 8,
                                    padding: '10px 16px', fontWeight: 600, fontSize: 12,
                                    cursor: profilingLoading ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                }}>
                                {profilingLoading ? (
                                    <>
                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                            <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Generating Profiles…
                                    </>
                                ) : "✦ Generate AI Profiles"}
                            </button>

                            {loadedRunInfo && (
                                <div style={{
                                    display: "flex", alignItems: "center", gap: 8,
                                    padding: "9px 12px", borderRadius: 10,
                                    background: C.green + "0C", border: `1px solid ${C.green}33`,
                                    fontSize: 11.5, color: C.text,
                                }}>
                                    <span style={{ fontSize: 14 }}>✓</span>
                                    <span>
                                        Loaded <strong>{loadedRunInfo.matched}</strong> of {loadedRunInfo.total} profiles into the customer table
                                        {loadedRunInfo.generatedAt && (
                                            <> · from {new Date(loadedRunInfo.generatedAt).toLocaleDateString("en-PH", { month: "short", day: "numeric" })}</>
                                        )}
                                        . Expand any customer row below to view their AI profile card.
                                    </span>
                                </div>
                            )}

                            {profilingHistoryLoading ? (
                                <Spinner height={160} />
                            ) : !profilingHistory?.length ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", textAlign: "center" }}>
                                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.muted + "18", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>🕓</div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No profiling runs yet</p>
                                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                        Generated AI profile batches will be saved here automatically.
                                    </p>
                                </div>
                            ) : profilingHistory.map((entry) => {
                                const isOpen = expandedProfilingId === entry.id
                                const generatedDate = new Date(entry.generated_at)
                                return (
                                    <div key={entry.id} style={{
                                        borderRadius: 12,
                                        border: `1px solid ${isOpen ? C.purple + "44" : C.border}`,
                                        overflow: "hidden", transition: "border-color 0.15s",
                                    }}>
                                        <div
                                            onClick={() => setExpandedProfilingId(isOpen ? null : entry.id)}
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                padding: "10px 14px",
                                                background: isOpen ? C.purple + "08" : C.dark,
                                                cursor: "pointer", gap: 10, transition: "background 0.15s",
                                            }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, background: C.purple + "15", borderRadius: 50, padding: "2px 8px" }}>
                                                    {entry.customer_count} customers
                                                </span>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                                                    {" at "}
                                                    {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                                                </p>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); loadProfilingRun(entry) }}
                                                    title="Load this profiling run into the customer table"
                                                    style={{
                                                        background: loadedProfilingId === entry.id ? C.purple : "none",
                                                        border: `1px solid ${loadedProfilingId === entry.id ? C.purple : C.border}`,
                                                        borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                                                        color: loadedProfilingId === entry.id ? "#fff" : C.purple,
                                                        fontSize: 10, fontWeight: 600,
                                                    }}>
                                                    {loadedProfilingId === entry.id ? "✓ Loaded" : "Load"}
                                                </button>
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation()
                                                        setDeletingProfilingId(entry.id)
                                                        await deleteProfilingRun(entry.id)
                                                        if (expandedProfilingId === entry.id) setExpandedProfilingId(null)
                                                        if (loadedProfilingId === entry.id) {
                                                            setLoadedProfilingId(null)
                                                            setLoadedRunInfo(null)
                                                        }
                                                        setDeletingProfilingId(null)
                                                    }}
                                                    disabled={deletingProfilingId === entry.id}
                                                    title="Delete this entry"
                                                    style={{
                                                        background: "none", border: `1px solid ${C.border}`,
                                                        borderRadius: 6, padding: "3px 6px",
                                                        cursor: deletingProfilingId === entry.id ? "not-allowed" : "pointer",
                                                        color: C.muted, display: "flex", alignItems: "center",
                                                        opacity: deletingProfilingId === entry.id ? 0.5 : 1,
                                                    }}>
                                                    {deletingProfilingId === entry.id ? (
                                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                                                            <path fill={C.muted} d="M4 12a8 8 0 018-8v8z"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                                                            <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                        </svg>
                                                    )}
                                                </button>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                                    fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2"
                                                    style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
                                                    <path d="M6 9l6 6 6-6"/>
                                                </svg>
                                            </div>
                                        </div>

                                        {isOpen && (
                                            <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
                                                {entry.profiles?.map((p, i) => (
                                                    <div key={i} style={{
                                                        padding: "8px 10px", borderRadius: 8,
                                                        background: C.dark, border: `1px solid ${C.border}`,
                                                    }}>
                                                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: C.text }}>
                                                            {p.fullName ?? p.userId}
                                                        </p>
                                                        <p style={{ margin: "2px 0 0", fontSize: 11, color: C.purple }}>
                                                            {p.personality ?? "—"}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}